import { faStar, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { Routes } from '../../classes/Routes';
import Alert from '../../components/UI/Alert/Alert';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Table from '../../components/UI/Table/Table';
import TableTh from '../../components/UI/Table/TableTh/TableTh';
import { useTableSort } from '../../components/UI/Table/useTableSort';
import Button, { EButtonColor } from '../../components/UI/Button/Button';
import ConfirmDialog from '../../components/UI/ConfirmDialog/ConfirmDialog';
import ModalContext, { defaultModal, EModalSize } from '../../components/UI/Modal/ModalContext';
import { IAppState } from '../../interfaces';
import { dateToDateTimeString, linkHandler } from '../../shared/utility';
import * as actions from '../../store/actions';
import { ETranslation } from '../../translations/translation-keys';
import classes from './Feedbacks.module.scss';

const Feedbacks: React.FC<RouteComponentProps> = ({ history }) => {
  const { loading, error, feedbacks } = useSelector((state: IAppState) => ({
    loading: state.feedbacks.loading,
    error: state.feedbacks.error,
    feedbacks: state.feedbacks.feedbacks,
  }));

  const { sort, onSort, items: sortedFeedbacks } = useTableSort({ 
    data: feedbacks,
    defaultSort: { key: 'created', reverse: true, type: 'date' }
  });
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const { setModal } = useContext(ModalContext);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(actions.listFeedbacks());
  }, [dispatch]);

  const showFeedback = (event: React.MouseEvent<HTMLTableRowElement>, id: string) => {
    linkHandler(event, Routes.OFFER_REQUEST(id), history);
  }

  const mapStars = (num?: number) => {
    if (!num) return null;
    return [...Array(num)].map((e, i) => <FontAwesomeIcon key={i} className={classes.Star} icon={faStar} />);
  }

  const displayedFeedbacks = sortedFeedbacks?.filter(f => {
    if (sourceFilter === 'all') return true;
    
    const isPublic = f.source === 'public' || !f.offerRequest;
    const isCustomer = f.source === 'customer' || (f.offerRequest && !f.source);
    const isServiceProvider = f.source === 'serviceProvider';

    if (sourceFilter === 'public') return isPublic;
    if (sourceFilter === 'customer') return isCustomer;
    if (sourceFilter === 'serviceProvider') return isServiceProvider;
    
    return f.source === sourceFilter;
  });

  const showRequestInfo = (e: React.MouseEvent, feedback: any) => {
    e.stopPropagation();
    setModal({
      isOpen: true,
      title: 'Tekniset tiedot (RequestInfo)',
      size: EModalSize.MEDIUM,
      content: (
        <div style={{ padding: '1rem' }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {Object.entries(feedback.requestInfo.properties).map(([key, val]: any) => {
              const displayVal = val.stringValue || (val.nullValue === null ? 'null' : (val.booleanValue !== undefined ? val.booleanValue.toString() : JSON.stringify(val)));
              return (
                <li key={key} style={{ marginBottom: '0.75rem', wordBreak: 'break-word', fontSize: '0.9rem' }}>
                  <strong style={{ display: 'block', color: '#555' }}>{key}:</strong>
                  <span style={{ color: displayVal === 'null' ? '#aaa' : '#000' }}>{displayVal}</span>
                </li>
              );
            })}
          </ul>
          <div style={{ marginTop: '2rem', textAlign: 'right' }}>
            <Button onClick={() => setModal(defaultModal)}>{t(ETranslation.COMMON_CLOSE) || 'Sulje'}</Button>
          </div>
        </div>
      ),
    });
  };

  const confirmDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setModal({
      isOpen: true,
      title: t(ETranslation.COMMON_REMOVE),
      size: EModalSize.SMALL,
      content: (
        <ConfirmDialog
          body={<p>{t(ETranslation.CONFIRM_DELETE_FEEDBACK)}</p>}
          onConfirm={() => {
            dispatch(actions.deleteFeedback(id));
            setModal(defaultModal);
          }}
          onCancel={() => setModal(defaultModal)}
        />
      ),
    });
  };

  return (
    <>
      {error && <Alert children={error} />}
      <Container>
        <h2>{t(ETranslation.TITLE_FEEDBACKS)}</h2>
        {loading
          ? <Spinner />
          : feedbacks && sortedFeedbacks
            ? (
              <>
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                  <label htmlFor="sourceFilter" style={{ marginRight: '1rem', fontWeight: 'bold' }}>{t(ETranslation.FEEDBACK_SOURCE)}:</label>
                  <select 
                    id="sourceFilter" 
                    value={sourceFilter} 
                    onChange={(e) => setSourceFilter(e.target.value)}
                    style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="all">{t(ETranslation.FEEDBACK_SOURCE_ALL)}</option>
                    <option value="public">{t(ETranslation.FEEDBACK_SOURCE_PUBLIC)}</option>
                    <option value="customer">{t(ETranslation.FEEDBACK_SOURCE_CUSTOMER)}</option>
                    <option value="serviceProvider">{t(ETranslation.FEEDBACK_SOURCE_SERVICEPROVIDER)}</option>
                  </select>
                </div>
                <Table hover sortContext={{ sort, onSort }}>
                <thead>
                  <tr>
                    <TableTh sort="source">{t(ETranslation.FEEDBACK_SOURCE)}</TableTh>
                    <TableTh sort="created" type="date">{t(ETranslation.COMMON_CREATED)}</TableTh>
                    <TableTh sort="offerRequest.firstName">{t(ETranslation.COMMON_NAME)}</TableTh>
                    <TableTh sort="offerRequest.id">{t(ETranslation.CUSTOMER_OFFER_REQUEST)}</TableTh>
                    <TableTh sort="offerRequest.service.name">{t(ETranslation.COMMON_SERVICE)}</TableTh>
                    <TableTh sort="message">{t(ETranslation.FEEDBACK_MESSAGE)}</TableTh>
                    <TableTh sort="serviceProvider.name">{t(ETranslation.FEEDBACK_SERVICEPROVIDER)}</TableTh>
                    <TableTh sort="serviceSatisfaction" type="int">{t(ETranslation.FEEDBACK_SATISFACTION)}</TableTh>
                    <TableTh sort="">Info</TableTh>
                    <TableTh sort="">{t(ETranslation.COMMON_REMOVE)}</TableTh>
                  </tr>
                </thead>
                <tbody>
                  {displayedFeedbacks?.map(feedback => {
                    const isPublic = feedback.source === 'public' || !feedback.offerRequest;
                    const isCustomer = feedback.source === 'customer' || (feedback.offerRequest && !feedback.source);
                    const isServiceProvider = feedback.source === 'serviceProvider';
                    
                    let sourceLabel = '-';
                    if (isPublic) sourceLabel = t(ETranslation.FEEDBACK_SOURCE_PUBLIC);
                    else if (isCustomer) sourceLabel = t(ETranslation.FEEDBACK_SOURCE_CUSTOMER);
                    else if (isServiceProvider) sourceLabel = t(ETranslation.FEEDBACK_SOURCE_SERVICEPROVIDER);

                    const serviceName = isPublic ? (feedback.tenantId || '') : (feedback.offerRequest?.service?.name || '');
                    const emailAddress = isPublic ? feedback.email : feedback.offerRequest?.email;
                    
                    const emailLink = emailAddress ? (
                      <a href={`mailto:${emailAddress}?subject=${encodeURIComponent(`HankiX ${serviceName} palautteesi`)}`} onClick={(e) => e.stopPropagation()}>
                        ({emailAddress})
                      </a>
                    ) : null;

                    return (
                    <tr key={feedback.id} onClick={(e) => feedback.offerRequest?.id ? showFeedback(e, feedback.offerRequest.id) : undefined} style={{ cursor: feedback.offerRequest?.id ? 'pointer' : 'default' }}>
                      <td>{sourceLabel}</td>
                      <td>{feedback.created ? dateToDateTimeString(new Date(feedback.created)) : '-'}</td>
                      <td>
                        {isPublic 
                          ? <>{feedback.name || ''} {emailLink}</> 
                          : <>{feedback.offerRequest?.firstName || ''} {feedback.offerRequest?.lastName || ''} {emailLink}</>}
                      </td>
                      <td>
                        {feedback.offerRequest?.id ? (
                          <a 
                            href={Routes.OFFER_REQUEST(feedback.offerRequest.id)} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            onClick={(e) => e.stopPropagation()}
                            style={{ textDecoration: 'underline' }}
                          >
                            {t(ETranslation.COMMON_SEARCH)}
                          </a>
                        ) : '-'}
                      </td>
                      <td>{serviceName}</td>
                      <td>{feedback.message}</td>
                      <td>
                        {isPublic
                          ? '-'
                          : feedback.serviceProvider
                            ? feedback.serviceProvider.name
                            : (feedback.otherFeedback || feedback.serviceProviderOther)
                              ? (feedback.otherFeedback || feedback.serviceProviderOther)
                              : t(ETranslation.FEEDBACK_FORM_NOT_FOUND)}
                      </td>
                      <td>{mapStars(feedback.serviceSatisfaction)}</td>
                      <td>
                        {feedback.requestInfo?.properties && Object.keys(feedback.requestInfo.properties).length > 0 && (
                          <Button 
                            style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                            onClick={(e) => showRequestInfo(e, feedback)}
                          >
                            Info
                          </Button>
                        )}
                      </td>
                      <td>
                        <Button 
                          style={{ padding: '0.2rem 0.5rem', fontSize: '0.8rem' }}
                          color={EButtonColor.DANGER}
                          onClick={(e) => confirmDelete(e, feedback.id)}
                          icon={faTrash}
                        />
                      </td>

                    </tr>
                    );
                  })}
                </tbody>
              </Table>
             </>
            )
            : <p>{t(ETranslation.NO_FEEDBACK)}</p>
        }
      </Container>
    </>
  );
}

export default Feedbacks;